/**
 * Tenant branding: the runtime half of the token contract.
 *
 * White-label tenants supply brand colours (`branding_profiles`), and the promise made by the
 * three-layer token system in ./styles/tokens.css is that honouring them is a variable swap.
 * That promise has a sharp edge: the values arrive from a tenant, are stored in the database, and
 * end up as CSS that renders for *every* recipient of that tenant's envelopes — including people
 * who have no relationship with the tenant. So this module treats them as untrusted input at two
 * levels:
 *
 *   1. NAME — only the semantic layer is writable. Primitives are the palette the product owns;
 *      component tokens are internal wiring. A tenant that could rewrite `--row-height` or
 *      `--focus-ring-width` could make the audit trail unreadable or the focus ring invisible,
 *      which is an accessibility and evidence problem, not a taste problem.
 *
 *   2. VALUE — only a colour, by an allowlist grammar. CSS custom properties are substituted
 *      textually, so an unvalidated value is a content-injection primitive: `url(...)` inside a
 *      property that reaches `background` fetches an attacker-chosen origin from the signing page
 *      and leaks the visit; `var(--x)` lets a value reach tokens it was never granted. Neither is
 *      hypothetical enough to allow. Hex, rgb/rgba, hsl/hsla and oklch/oklab with numeric
 *      arguments cover every case a brand palette actually needs.
 *
 * Rejections are returned, never thrown and never silently dropped: the caller logs them to the
 * audit stream so a tenant whose branding is being ignored can be told why.
 */

/**
 * The semantic tokens a tenant may set. This list is the security boundary — adding a name here
 * grants tenants control of it, so add deliberately. Kept in step with tokens.css by
 * test/tokens.test.ts, which fails if either side gains a token the other lacks.
 */
// The token list and the colour grammar now live in @flowza/core: the API needs them to decide
// whether to STORE a value, and a Worker must not import React to validate a hex string. Kept
// re-exported here so the design system has one import surface and tokens.test.ts still holds
// this list to theme.css.
export {
  BRANDABLE_TOKENS,
  isBrandableColor,
  isBrandableToken,
  type BrandableToken,
} from '@flowza/core/branding'
import {
  BRANDABLE_TOKENS,
  isBrandableColor,
  isBrandableToken,
  normaliseTokenName,
  readableOnAccent,
  type BrandableToken,
} from '@flowza/core/branding'

/* ------------------------------------------------------------------------------- applying --- */

export type BrandingRejection = {
  readonly token: string
  readonly reason: 'not-brandable' | 'invalid-color'
}

export type BrandingResult = {
  /** Tokens the TENANT supplied and that passed both checks. */
  readonly applied: readonly BrandableToken[]
  readonly rejected: readonly BrandingRejection[]
  /**
   * Tokens written from another token rather than supplied — currently only `accent-hover`.
   *
   * Kept out of `applied` on purpose: the API logs a profile that applied nothing as
   * `ceremony_branding_empty`, and a derived token must not make an all-rejected profile look
   * like a working one.
   */
  readonly derived: readonly BrandableToken[]
}

/** The minimum of `CSSStyleDeclaration` this needs — so it is testable without a DOM. */
export type TokenTarget = {
  setProperty(property: string, value: string): void
  removeProperty(property: string): string
}

/**
 * How far a derived hover sits from the accent it comes from: 15% of the way to black.
 *
 * Enough to read as a state change on every hue we can be handed, small enough that it never
 * crosses into looking like a second colour.
 */
const HOVER_MIX = 'color-mix(in srgb, ACCENT 85%, black)'

/**
 * `color-mix` is the only way to darken a colour we hold as TEXT rather than as channels. A tenant
 * may give us hex, `rgb()`, `hsl()` or `oklch()`, and parsing all four to do the arithmetic here
 * would put a colour library in the bundle a stranger downloads from an email link.
 */
function supportsColorMix(): boolean {
  const css = (globalThis as { CSS?: { supports?(property: string, value: string): boolean } }).CSS
  if (typeof css?.supports !== 'function') return false
  try {
    return css.supports('color', 'color-mix(in srgb, red 50%, black)')
  } catch {
    return false
  }
}

/**
 * The hover state for an accent the tenant set without setting a hover of their own.
 *
 * Where `color-mix` is unsupported this returns the accent unchanged: no hover feedback is a
 * smaller defect than hover feedback that jumps to a colour from somebody else's brand.
 *
 * Safe to interpolate because `accent` has already passed `isBrandableColor`, whose grammar
 * forbids `;`, braces, quotes and backslashes outright and anchors the only legal parentheses to a
 * colour function's own two positions — there is no value it admits that can close this `color-mix`
 * early.
 */
function derivedHover(accent: string): string {
  return supportsColorMix() ? HOVER_MIX.replace('ACCENT', accent) : accent
}

/**
 * Write a tenant's branding onto `target` (in the browser, `document.documentElement.style`).
 *
 * Every entry is checked by name and by value; anything that fails is reported rather than
 * applied, and the corresponding token keeps its product default. A tenant cannot make the
 * product unusable by supplying rubbish, and cannot reach a token it was not granted.
 */
export function applyBranding(
  target: TokenTarget,
  branding: Readonly<Record<string, string>>,
): BrandingResult {
  const applied: BrandableToken[] = []
  const rejected: BrandingRejection[] = []
  const derived: BrandableToken[] = []
  let accentValue: string | null = null

  for (const [rawName, rawValue] of Object.entries(branding)) {
    const name = normaliseTokenName(rawName)
    if (!isBrandableToken(name)) {
      rejected.push({ token: name, reason: 'not-brandable' })
      continue
    }
    if (typeof rawValue !== 'string' || !isBrandableColor(rawValue)) {
      rejected.push({ token: name, reason: 'invalid-color' })
      continue
    }
    const value = rawValue.trim()
    target.setProperty(`--${name}`, value)
    applied.push(name)
    if (name === 'accent') accentValue = value
  }

  // A tenant who sets `accent` and nothing else is the ORDINARY case, not an edge one:
  // `org_branding_profiles.primary_color` is a single column and folds into exactly this token. It
  // used to produce a button painted in the tenant's colour that turned product BLUE under the
  // cursor, because `--accent-hover` carries its own default (`--color-blue-700`) and nothing tied
  // it to the accent beside it. Verified on the deployed ceremony: `--accent` was `#059669` while
  // `--accent-hover` stayed `#2249b0`.
  //
  // `focus-ring` is deliberately NOT derived. A hover is decoration on an element already painted
  // in the accent, so an accent legible enough to sit under white text is legible hovered; a focus
  // ring is an accessibility affordance that has to stay visible against `surface-base`, and
  // repainting it in a colour the tenant chose for a different job is how it goes invisible. A
  // tenant who wants a branded ring can still set `focus-ring` — it is in the allowlist.
  // The LABEL on that button, by the same argument and a stronger one.
  //
  // `text-on-accent` defaults to white, and white does not read on every brand colour: the tenant
  // in production is #059669, and white on it is 3.77:1 — under the 4.5:1 WCAG 2.2 AA asks for
  // normal text, on the button that finishes a signature. Ink on the same green is 5.57:1.
  //
  // The accent belongs to the TENANT and the label colour belongs to us, so when the two disagree
  // the one to move is ours. `PUT /v1/organization/branding` refuses a palette whose contrast
  // fails, but a row stored before that rule existed is still out there — deriving here is what
  // makes those render legibly rather than waiting for someone to re-save.
  if (accentValue !== null && !applied.includes('text-on-accent')) {
    const readable = readableOnAccent(accentValue)
    if (readable !== null) {
      target.setProperty('--text-on-accent', readable)
      derived.push('text-on-accent')
    }
  }

  if (accentValue !== null && !applied.includes('accent-hover')) {
    target.setProperty('--accent-hover', derivedHover(accentValue))
    derived.push('accent-hover')
  }

  return { applied, rejected, derived }
}

/** Drop every tenant override, returning the surface to product defaults. */
export function clearBranding(target: TokenTarget): void {
  for (const token of BRANDABLE_TOKENS) target.removeProperty(`--${token}`)
}

/**
 * The DocuSign navy, and the only reason it is a constant in this file.
 *
 * ADR 0063 measured it on the product's own surfaces — 8.91:1 against the label, 8.61:1 against the
 * page — which is why it is the one competitor colour this product will paint. Their yellow is
 * unreadable against a page and is deliberately not offered anywhere.
 *
 * It lives here rather than only in `Settings.tsx`'s palette list because the palette RESOLUTION
 * below needs it, and `Settings.tsx` is a screen. A colour the console paints on first render, from
 * a cache, before any screen has mounted, cannot be defined inside a screen.
 */
export const DOCUSIGN_ACCENT = '#004b87'

/**
 * Which accent a console should paint, given what the tenant set and which convention they work to.
 *
 * THE ORDER IS THE DECISION, and it is: a colour the tenant CHOSE beats the standard's palette,
 * which beats the product's own. Each step is a narrower statement of intent than the one after it.
 *
 *   1. `primary_color` — somebody opened Branding and picked this. Nothing overrides a choice.
 *   2. `international` — FlowZa put this tenant on the DocuSign standard and they have chosen no
 *      colour, so the standard's palette is the most specific thing anybody has said.
 *   3. null — "FlowZa is the absence of an override" (ADR 0060). Not the product hex: returning it
 *      would write `--accent` to the same value the stylesheet already holds, which looks identical
 *      and is not the same thing, because a written token survives a stylesheet change and an
 *      absent one follows it.
 *
 * Null is therefore a real answer meaning "paint nothing", and every caller must treat it as one.
 */
export function resolveAccent(input: {
  readonly primaryColor: string | null | undefined
  readonly workflowStandard: string | null | undefined
}): string | null {
  const chosen = typeof input.primaryColor === 'string' ? input.primaryColor.trim() : ''
  if (chosen !== '') return chosen
  if (input.workflowStandard === 'international') return DOCUSIGN_ACCENT
  return null
}

/**
 * Paint the resolved accent, or clear back to the product's own.
 *
 * One function so the "null means clear" rule cannot be got wrong by a caller who forgot it. A
 * console that applied `resolveAccent`'s null by doing nothing would leave the previous tenant's
 * colour painted after an org switch, which is exactly the bug a shared machine produces.
 *
 * ## It clears FIRST, and the earlier version did not
 *
 * This used to clear only when the whole token map came out empty. Two ways that left a colour on
 * the document that nobody had chosen, both of them the very failure the paragraph above claims to
 * prevent:
 *
 *   1. a null accent arriving alongside ANY other token — say a tenant who set `focus-ring` and
 *      nothing else — skipped the clear, so an `--accent` written by a previous paint stayed on the
 *      style attribute and kept painting. Reachable through the org-switch repaint and through the
 *      workflow-standard repaint, neither of which goes through a screen.
 *   2. an accent REJECTED by `applyBranding`'s grammar — a legacy row written before that grammar
 *      existed, which `serveCeremonyBranding` says out loud can exist — applied nothing and cleared
 *      nothing, so the document kept whatever was there while the caller was told it was saved.
 *
 * Clearing unconditionally before applying costs one pass over a seven-name list and makes the
 * post-state a function of the INPUT alone rather than of the input plus whatever was painted
 * before. That is the property every caller here already assumed it had.
 */
export function applyResolvedAccent(
  target: TokenTarget,
  input: {
    readonly primaryColor: string | null | undefined
    readonly workflowStandard: string | null | undefined
    readonly themeTokens?: Readonly<Record<string, string>> | null
  },
): BrandingResult {
  const accent = resolveAccent(input)
  const tokens: Record<string, string> = { ...(input.themeTokens ?? {}) }
  if (accent !== null && tokens['accent'] === undefined) tokens['accent'] = accent

  // Unconditional, and before anything is written. See the note above.
  clearBranding(target)

  if (Object.keys(tokens).length === 0) {
    return { applied: [], rejected: [], derived: [] }
  }
  return applyBranding(target, tokens)
}
